import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, Star, Trash2, ExternalLink, CloudSun, AlertTriangle, TrendingUp, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { PageHeader } from '../components/layout/PageHeader';
import { getFavoritos, saveFavoritos, getConfig, saveConfig, clearAllStorage } from '../services/storage';
import { clearCache } from '../services/ckanClient';
import { toast } from 'sonner';

// Estaciones meteorológicas del Cabildo
const ESTACIONES_MOCK = [
  { id: 'TEJINA01', nombre: 'Tejina' },
  { id: 'TEGUESTE', nombre: 'Tegueste' },
  { id: 'TACORONTE', nombre: 'Tacoronte' },
  { id: 'SAUZAL', nombre: 'El Sauzal' },
  { id: 'MATANZA', nombre: 'La Matanza' },
  { id: 'VICTORIA', nombre: 'La Victoria' },
  { id: 'SANTAURS', nombre: 'Santa Úrsula' },
  { id: 'OROTAVA', nombre: 'La Orotava' },
  { id: 'REALEJ01', nombre: 'Los Realejos' },
  { id: 'PNEGRITO', nombre: 'Puerto de la Cruz' },
  { id: 'SJRAMBLA', nombre: 'San Juan de la Rambla' },
  { id: 'ICOD', nombre: 'Icod de los Vinos' },
  { id: 'GARACHIC', nombre: 'Garachico' },
  { id: 'SILOS', nombre: 'Los Silos' },
  { id: 'BUENAVST', nombre: 'Buenavista del Norte' },
  { id: 'GUIMAR', nombre: 'Güímar' },
  { id: 'FASNIA', nombre: 'Fasnia' },
  { id: 'ARICO', nombre: 'Arico' },
  { id: 'GRANADIL', nombre: 'Granadilla de Abona' },
  { id: 'SMIGU01', nombre: 'San Miguel de Abona' },
  { id: 'VILAFLOR', nombre: 'Vilaflor' },
  { id: 'ARONA', nombre: 'Arona' },
  { id: 'ADEJE', nombre: 'Adeje' },
  { id: 'GUIAISOR', nombre: 'Guía de Isora' },
  { id: 'STGO_SUR', nombre: 'Santiago del Teide' },
  { id: 'LAGUNA', nombre: 'La Laguna' },
  { id: 'SCTF', nombre: 'Santa Cruz de Tenerife' },
  { id: 'CANDELAR', nombre: 'Candelaria' },
  { id: 'ARAFO', nombre: 'Arafo' },
  { id: 'ANAGA', nombre: 'Anaga' },
  { id: 'TEIDE', nombre: 'Parque Nacional del Teide' },
  { id: 'IZANA', nombre: 'Izaña' },
];

const COMARCAS_VINA = [
  { id: 'tacoronte-acentejo', nombre: 'Tacoronte-Acentejo' },
  { id: 'valle-orotava', nombre: 'Valle de La Orotava' },
  { id: 'ycoden-daute-isora', nombre: 'Ycoden-Daute-Isora' },
  { id: 'abona', nombre: 'Abona' },
  { id: 'guimar', nombre: 'Valle de Güímar' },
];

const COMARCAS_PAPA = [
  { id: 'norte', nombre: 'Comarca Norte' },
  { id: 'sur', nombre: 'Comarca Sur' },
  { id: 'medianias', nombre: 'Medianías' },
];

const PRODUCTOS = [
  'Tomate', 'Papa', 'Plátano', 'Aguacate', 'Cebolla', 'Lechuga', 'Zanahoria', 'Pimiento'
];

export function AjustesPage() {
  const navigate = useNavigate();
  const [favoritos, setFavoritos] = useState(getFavoritos());
  const [config, setConfig] = useState(getConfig());
  const [hasChanges, setHasChanges] = useState(false);

  function handleFavoritoChange(key, value) {
    setFavoritos(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  }

  function handleConfigChange(key, value) {
    setConfig(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  }

  function handleSave() {
    saveFavoritos(favoritos);
    saveConfig(config);
    setHasChanges(false);
    toast.success('Ajustes guardados correctamente');
  }

  async function handleClearCache() {
    try {
      await clearCache();
      toast.success('Caché limpiada correctamente');
    } catch (e) {
      toast.error('Error al limpiar caché');
    }
  }

  function handleClearAll() {
    if (window.confirm('¿Seguro que quieres borrar todos los datos guardados? Esta acción no se puede deshacer.')) {
      clearAllStorage();
      clearCache();
      setFavoritos(getFavoritos());
      setConfig(getConfig());
      toast.success('Todos los datos han sido borrados');
    }
  }

  // Verificar si hay favoritos guardados
  const hasFavoritos = favoritos.estacionId || favoritos.comarcaVina || favoritos.comarcaPapa || favoritos.productoFavorito;

  return (
    <div className="px-4 py-4" data-testid="ajustes-page">
      <PageHeader 
        title="Ajustes" 
        subtitle="Configurar favoritos y preferencias"
        showBack
      />

      <div className="mt-4 space-y-4">
        {/* Acceso rápido a favoritos */}
        {hasFavoritos && (
          <Card className="bg-primary/5 border-primary/20">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Star size={18} className="text-primary fill-primary" />
                Acceso rápido
              </CardTitle>
              <CardDescription>
                Ir directamente a tus recursos favoritos
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {favoritos.estacionId && (
                <Button 
                  variant="outline" 
                  className="w-full justify-between bg-white"
                  onClick={() => navigate('/agroclima')}
                  data-testid="goto-estacion"
                >
                  <span className="flex items-center gap-2">
                    <CloudSun size={16} className="text-primary" />
                    {favoritos.estacionNombre || favoritos.estacionId}
                  </span>
                  <ArrowRight size={16} />
                </Button>
              )}
              
              {favoritos.comarcaVina && (
                <Button 
                  variant="outline" 
                  className="w-full justify-between bg-white"
                  onClick={() => navigate('/avisos')}
                  data-testid="goto-comarca-vina"
                >
                  <span className="flex items-center gap-2">
                    <AlertTriangle size={16} className="text-amber-500" />
                    Avisos viña: {COMARCAS_VINA.find(c => c.id === favoritos.comarcaVina)?.nombre || favoritos.comarcaVina}
                  </span>
                  <ArrowRight size={16} />
                </Button>
              )}
              
              {favoritos.comarcaPapa && (
                <Button 
                  variant="outline" 
                  className="w-full justify-between bg-white"
                  onClick={() => navigate('/avisos')}
                  data-testid="goto-comarca-papa"
                >
                  <span className="flex items-center gap-2">
                    <AlertTriangle size={16} className="text-amber-500" />
                    Avisos papa: {COMARCAS_PAPA.find(c => c.id === favoritos.comarcaPapa)?.nombre || favoritos.comarcaPapa}
                  </span>
                  <ArrowRight size={16} />
                </Button>
              )}
              
              {favoritos.productoFavorito && (
                <Button 
                  variant="outline" 
                  className="w-full justify-between bg-white"
                  onClick={() => navigate('/precios')}
                  data-testid="goto-producto"
                >
                  <span className="flex items-center gap-2">
                    <TrendingUp size={16} className="text-green-600" />
                    Precios: {favoritos.productoFavorito}
                  </span>
                  <ArrowRight size={16} />
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Favoritos */}
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Star size={18} className="text-primary" />
              Favoritos
            </CardTitle>
            <CardDescription>
              Configura tus estaciones y productos preferidos
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Estación favorita */}
            <div className="space-y-2">
              <Label htmlFor="estacion">Estación agroclimática favorita</Label>
              <Select 
                value={favoritos.estacionId || 'none'} 
                onValueChange={(v) => {
                  const value = v === 'none' ? null : v;
                  const est = ESTACIONES_MOCK.find(e => e.id === value);
                  handleFavoritoChange('estacionId', value);
                  handleFavoritoChange('estacionNombre', est?.nombre || null);
                }}
              >
                <SelectTrigger id="estacion" data-testid="select-estacion" className="bg-white">
                  <SelectValue placeholder="Seleccionar estación" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Ninguna</SelectItem>
                  {ESTACIONES_MOCK.map(est => (
                    <SelectItem key={est.id} value={est.id}>
                      {est.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Comarca viña */}
            <div className="space-y-2">
              <Label htmlFor="comarca-vina">Comarca viña favorita</Label>
              <Select 
                value={favoritos.comarcaVina || 'none'} 
                onValueChange={(v) => handleFavoritoChange('comarcaVina', v === 'none' ? null : v)}
              >
                <SelectTrigger id="comarca-vina" data-testid="select-comarca-vina" className="bg-white">
                  <SelectValue placeholder="Seleccionar comarca" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Ninguna</SelectItem>
                  {COMARCAS_VINA.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Comarca papa */}
            <div className="space-y-2">
              <Label htmlFor="comarca-papa">Comarca papa favorita</Label>
              <Select 
                value={favoritos.comarcaPapa || 'none'} 
                onValueChange={(v) => handleFavoritoChange('comarcaPapa', v === 'none' ? null : v)}
              >
                <SelectTrigger id="comarca-papa" data-testid="select-comarca-papa" className="bg-white">
                  <SelectValue placeholder="Seleccionar comarca" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Ninguna</SelectItem>
                  {COMARCAS_PAPA.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Producto favorito */}
            <div className="space-y-2">
              <Label htmlFor="producto">Producto favorito</Label>
              <Select 
                value={favoritos.productoFavorito || 'none'} 
                onValueChange={(v) => handleFavoritoChange('productoFavorito', v === 'none' ? null : v)}
              >
                <SelectTrigger id="producto" data-testid="select-producto" className="bg-white">
                  <SelectValue placeholder="Seleccionar producto" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Ninguno</SelectItem>
                  {PRODUCTOS.map(p => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Año por defecto */}
            <div className="space-y-2">
              <Label htmlFor="anio">Año por defecto (precios)</Label>
              <Select 
                value={favoritos.anioDefecto?.toString() || '2026'} 
                onValueChange={(v) => handleFavoritoChange('anioDefecto', parseInt(v))}
              >
                <SelectTrigger id="anio" data-testid="select-anio" className="bg-white">
                  <SelectValue placeholder="Seleccionar año" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2024">2024</SelectItem>
                  <SelectItem value="2025">2025</SelectItem>
                  <SelectItem value="2026">2026</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Preferencias */}
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="text-base">Preferencias</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="modo-ahorro">Modo ahorro de datos</Label>
                <p className="text-sm text-muted-foreground">
                  Reduce el número de registros consultados
                </p>
              </div>
              <Switch
                id="modo-ahorro"
                checked={config.modoAhorroDatos}
                onCheckedChange={(v) => handleConfigChange('modoAhorroDatos', v)}
                data-testid="switch-modo-ahorro"
              />
            </div>
          </CardContent>
        </Card>

        {/* Guardar */}
        {hasChanges && (
          <Button 
            className="w-full" 
            onClick={handleSave}
            data-testid="save-btn"
          >
            <Save size={16} className="mr-2" />
            Guardar cambios
          </Button>
        )}

        {/* Acciones de datos */}
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="text-base">Datos</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button 
              variant="outline" 
              className="w-full justify-start"
              onClick={handleClearCache}
              data-testid="clear-cache-btn"
            >
              <Trash2 size={16} className="mr-2" />
              Limpiar caché
            </Button>
            <Button 
              variant="destructive" 
              className="w-full justify-start"
              onClick={handleClearAll}
              data-testid="clear-all-btn"
            >
              <Trash2 size={16} className="mr-2" />
              Borrar todos los datos
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default AjustesPage;
